import type { Bounds, Connection, ConnectionId, ObjectId, Vec2, WorkspaceDocument } from '@/types';
import { connectionSegment, distanceToSegment } from '@/core/graph/connectionGeometry';

/** Resolves the drawn endpoints of a connection given how anchors and bounds are looked up. */
export const connectionEndpoints = (
  c: Connection,
  anchorOf: (id: ObjectId) => ObjectId,
  boundsOf: (id: ObjectId) => Bounds | undefined,
): { readonly a: Vec2; readonly b: Vec2 } | null => {
  const sa = anchorOf(c.sourceId);
  const ta = anchorOf(c.targetId);
  if (sa === ta) return null; // both ends collapsed into the same container: nothing to draw
  const sb = boundsOf(sa);
  const tb = boundsOf(ta);
  return sb && tb ? connectionSegment(sb, tb) : null;
};

/** Nearest connection within `tolerance` world units of a point, or null. */
export const hitTestConnections = (
  doc: WorkspaceDocument,
  point: Vec2,
  tolerance: number,
  anchorOf: (id: ObjectId) => ObjectId,
  boundsOf: (id: ObjectId) => Bounds | undefined,
): ConnectionId | null => {
  let best: ConnectionId | null = null;
  let bestDistance = tolerance;
  for (const c of Object.values(doc.connections)) {
    const seg = connectionEndpoints(c, anchorOf, boundsOf);
    if (!seg) continue;
    const d = distanceToSegment(point, seg.a, seg.b);
    if (d <= bestDistance) {
      bestDistance = d;
      best = c.id;
    }
  }
  return best;
};
